import type { Task, VehicleNodes, RiskItem, FunctionalTask, FunctionalNodeType } from '@/types';

export interface RiskRuleDefinition {
  id: string;
  name: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  phase: 'productDevelopment' | 'toolingLine';
  dependencies: {
    type: 'functionalTask' | 'functionalNode';
    name: FunctionalTask | FunctionalNodeType;
  }[];
  condition: {
    compareType: 'task_vs_node' | 'task_vs_task';
    taskA: FunctionalTask;
    nodeB?: FunctionalNodeType;
    taskB?: FunctionalTask;
    operator: '>' | '<';
    offsetWeeks?: number;
    useEndDate?: boolean;
  };
}

export const riskRuleDefinitions: RiskRuleDefinition[] = [
  {
    id: 'risk-pd-1',
    name: 'First Build Cannot Use Molded Parts',
    description: 'Product T0 task is later than First Build milestone',
    severity: 'high',
    phase: 'productDevelopment',
    dependencies: [
      { type: 'functionalTask', name: '产品T0' },
      { type: 'functionalNode', name: '首次造车' },
    ],
    condition: {
      compareType: 'task_vs_node',
      taskA: '产品T0',
      nodeB: '首次造车',
      operator: '>',
      useEndDate: true,
    },
  },
  {
    id: 'risk-pd-2',
    name: 'Dimensional Quality Risk',
    description: 'Product T0 task is later than First Build minus 8 weeks',
    severity: 'medium',
    phase: 'productDevelopment',
    dependencies: [
      { type: 'functionalTask', name: '产品T0' },
      { type: 'functionalNode', name: '首次造车' },
    ],
    condition: {
      compareType: 'task_vs_node',
      taskA: '产品T0',
      nodeB: '首次造车',
      operator: '>',
      offsetWeeks: -8,
      useEndDate: true,
    },
  },
  {
    id: 'risk-pd-3',
    name: 'Appearance Risk - No Texture on Saleable Vehicle',
    description: 'Texture processing task is later than Saleable minus 4 weeks',
    severity: 'medium',
    phase: 'productDevelopment',
    dependencies: [
      { type: 'functionalTask', name: '皮纹加工' },
      { type: 'functionalNode', name: '可销售车' },
    ],
    condition: {
      compareType: 'task_vs_node',
      taskA: '皮纹加工',
      nodeB: '可销售车',
      operator: '>',
      offsetWeeks: -4,
      useEndDate: true,
    },
  },
  {
    id: 'risk-pd-4',
    name: 'Appearance Approval Risk',
    description: 'Appearance approval is later than Saleable minus 4 weeks',
    severity: 'medium',
    phase: 'productDevelopment',
    dependencies: [
      { type: 'functionalTask', name: '外观认可' },
      { type: 'functionalNode', name: '可销售车' },
    ],
    condition: {
      compareType: 'task_vs_node',
      taskA: '外观认可',
      nodeB: '可销售车',
      operator: '>',
      offsetWeeks: -4,
      useEndDate: true,
    },
  },
  {
    id: 'risk-pd-5',
    name: 'Test Certification Risk',
    description: 'Product test is later than Saleable minus 2 weeks',
    severity: 'medium',
    phase: 'productDevelopment',
    dependencies: [
      { type: 'functionalTask', name: '产品试验' },
      { type: 'functionalNode', name: '可销售车' },
    ],
    condition: {
      compareType: 'task_vs_node',
      taskA: '产品试验',
      nodeB: '可销售车',
      operator: '>',
      offsetWeeks: -2,
      useEndDate: true,
    },
  },
  {
    id: 'risk-pd-6',
    name: 'Regulation Certification Risk',
    description: 'Regulation certification is later than Saleable minus 2 weeks',
    severity: 'medium',
    phase: 'productDevelopment',
    dependencies: [
      { type: 'functionalTask', name: '法规认证' },
      { type: 'functionalNode', name: '可销售车' },
    ],
    condition: {
      compareType: 'task_vs_node',
      taskA: '法规认证',
      nodeB: '可销售车',
      operator: '>',
      offsetWeeks: -2,
      useEndDate: true,
    },
  },
  {
    id: 'risk-tl-1',
    name: 'Fixture Development Risk',
    description: 'Fixture completion is later than Product T0',
    severity: 'high',
    phase: 'toolingLine',
    dependencies: [
      { type: 'functionalTask', name: '检具完成' },
      { type: 'functionalTask', name: '产品T0' },
    ],
    condition: {
      compareType: 'task_vs_task',
      taskA: '检具完成',
      taskB: '产品T0',
      operator: '>',
      useEndDate: true,
    },
  },
  {
    id: 'risk-tl-2',
    name: 'Production Line Risk - Cannot Use Formal Line for Delivery',
    description: 'Line completion is later than First Build',
    severity: 'high',
    phase: 'toolingLine',
    dependencies: [
      { type: 'functionalTask', name: '产线完成' },
      { type: 'functionalNode', name: '首次造车' },
    ],
    condition: {
      compareType: 'task_vs_node',
      taskA: '产线完成',
      nodeB: '首次造车',
      operator: '>',
      useEndDate: true,
    },
  },
  {
    id: 'risk-tl-3',
    name: 'Production Line Risk - PPAP Cannot Use Formal Line',
    description: 'Line completion is later than Saleable minus 2 weeks',
    severity: 'high',
    phase: 'toolingLine',
    dependencies: [
      { type: 'functionalTask', name: '产线完成' },
      { type: 'functionalNode', name: '可销售车' },
    ],
    condition: {
      compareType: 'task_vs_node',
      taskA: '产线完成',
      nodeB: '可销售车',
      operator: '>',
      offsetWeeks: -2,
      useEndDate: true,
    },
  },
  {
    id: 'risk-tl-4',
    name: 'Mold Development Risk - Cannot Use Formal Production',
    description: 'Mold completion is later than First Build',
    severity: 'high',
    phase: 'toolingLine',
    dependencies: [
      { type: 'functionalTask', name: '模具完成' },
      { type: 'functionalNode', name: '首次造车' },
    ],
    condition: {
      compareType: 'task_vs_node',
      taskA: '模具完成',
      nodeB: '首次造车',
      operator: '>',
      useEndDate: true,
    },
  },
  {
    id: 'risk-tl-5',
    name: 'Mold Development Risk - PPAP Cannot Use Formal Production',
    description: 'Mold completion is later than Saleable minus 2 weeks',
    severity: 'high',
    phase: 'toolingLine',
    dependencies: [
      { type: 'functionalTask', name: '模具完成' },
      { type: 'functionalNode', name: '可销售车' },
    ],
    condition: {
      compareType: 'task_vs_node',
      taskA: '模具完成',
      nodeB: '可销售车',
      operator: '>',
      offsetWeeks: -2,
      useEndDate: true,
    },
  },
];

export const functionalTaskDescriptions: Record<FunctionalTask, string> = {
  '产品T0': '模具件首次试制，总成T0',
  '产品试验': 'GMW14906灯具测试',
  '电子试验': 'EMC和3172测试',
  '法规认证': '法规认证测试，强检/CCC',
  '皮纹加工': 'AAR皮纹加工',
  '外观认可': '外观认可完成',
  '检具完成': '检具调试转移完成',
  '产线完成': '产线调试完成',
  '模具完成': '模具调试转移完成',
  '-': '无',
};

export interface FunctionalMapping {
  nodes: Partial<Record<FunctionalNodeType, string>>;
  tasks: Partial<Record<FunctionalTask, string>>;
}

export type TaskMatchingCallback = (functionalTask: FunctionalTask) => Promise<string | null>;

function getTaskDate(
  tasks: Task[],
  taskName: string | undefined,
  useEndDate: boolean = true
): Date | null {
  if (!taskName) return null;
  const task = tasks.find(t => t.name === taskName);
  if (!task) return null;
  return new Date(useEndDate ? task.end : task.start);
}

function getNodeDate(
  nodes: VehicleNodes,
  nodeMapping: Partial<Record<FunctionalNodeType, string>>
): (functionalNode: FunctionalNodeType) => Date | null {
  return (functionalNode: FunctionalNodeType) => {
    const actualNodeName = nodeMapping[functionalNode];
    if (!actualNodeName) return null;
    const nodeKey = actualNodeName as keyof VehicleNodes;
    const dateStr = nodes[nodeKey];
    if (!dateStr) return null;
    return new Date(dateStr);
  };
}

function addWeeks(date: Date, weeks: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + weeks * 7);
  return result;
}

function compareDates(dateA: Date, dateB: Date, operator: '>' | '<'): boolean {
  if (operator === '>') {
    return dateA > dateB;
  }
  return dateA < dateB;
}

export async function analyzeRisks(
  tasks: Task[],
  nodes: VehicleNodes,
  functionalMapping: FunctionalMapping,
  onNeedMatching?: TaskMatchingCallback
): Promise<RiskItem[]> {
  const risks: RiskItem[] = [];
  const taskMapping = { ...functionalMapping.tasks };
  const getNodeDateFn = getNodeDate(nodes, functionalMapping.nodes);

  for (const rule of riskRuleDefinitions) {
    let taskAName = taskMapping[rule.condition.taskA];

    if (!taskAName && onNeedMatching) {
      const matchedName = await onNeedMatching(rule.condition.taskA);
      if (matchedName) {
        taskAName = matchedName;
        taskMapping[rule.condition.taskA] = taskAName;
      }
    }

    if (!taskAName) continue;

    const taskADate = getTaskDate(tasks, taskAName, rule.condition.useEndDate);
    if (!taskADate) continue;

    const taskA = tasks.find(t => t.name === taskAName);

    let compareDate: Date | null = null;
    let compareNodeName: string | undefined = undefined;

    if (rule.condition.compareType === 'task_vs_node' && rule.condition.nodeB) {
      compareDate = getNodeDateFn(rule.condition.nodeB);
      compareNodeName = functionalMapping.nodes[rule.condition.nodeB];
    } else if (rule.condition.compareType === 'task_vs_task' && rule.condition.taskB) {
      let taskBName = taskMapping[rule.condition.taskB];

      if (!taskBName && onNeedMatching) {
        const matchedName = await onNeedMatching(rule.condition.taskB);
        if (matchedName) {
          taskBName = matchedName;
          taskMapping[rule.condition.taskB] = taskBName;
        }
      }

      if (taskBName) {
        compareDate = getTaskDate(tasks, taskBName, rule.condition.useEndDate);
        compareNodeName = taskBName;
      }
    }

    if (!compareDate) continue;

    if (rule.condition.offsetWeeks !== undefined) {
      compareDate = addWeeks(compareDate, rule.condition.offsetWeeks);
    }

    if (compareDates(taskADate, compareDate, rule.condition.operator)) {
      risks.push({
        id: rule.id,
        name: rule.name,
        description: rule.description,
        severity: rule.severity,
        phase: rule.phase,
        taskAName: taskA?.name || taskAName,
        taskADate: taskA?.end,
        nodeBName: compareNodeName || undefined,
        nodeBDate: compareDate ? compareDate.toISOString().split('T')[0] : undefined,
      });
    }
  }

  return risks;
}

export function analyzeAllRisks(
  tasks: Task[],
  nodes: VehicleNodes
): RiskItem[] {
  const risks: RiskItem[] = [];
  const functionalMapping: FunctionalMapping = {
    nodes: {
      '项目立项': 'VPI',
      '数据发布': 'VDR',
      '首次造车': 'APPV',
      '可销售车': 'S'
    },
    tasks: {},
  };
  const getNodeDateFn = getNodeDate(nodes, functionalMapping.nodes);

  for (const rule of riskRuleDefinitions) {
    const taskA = tasks.find(t => t.functionalTask === rule.condition.taskA);
    if (!taskA) continue;

    const taskADate = getTaskDate(tasks, taskA.name, rule.condition.useEndDate);
    if (!taskADate) continue;

    let compareDate: Date | null = null;
    let compareNodeName: string | undefined = undefined;

    if (rule.condition.compareType === 'task_vs_node' && rule.condition.nodeB) {
      compareDate = getNodeDateFn(rule.condition.nodeB);
      compareNodeName = functionalMapping.nodes[rule.condition.nodeB];
    } else if (rule.condition.compareType === 'task_vs_task' && rule.condition.taskB) {
      const taskB = tasks.find(t => t.functionalTask === rule.condition.taskB);
      if (taskB) {
        compareDate = getTaskDate(tasks, taskB.name, rule.condition.useEndDate);
        compareNodeName = taskB.name;
      }
    }

    if (!compareDate) continue;

    if (rule.condition.offsetWeeks !== undefined) {
      compareDate = addWeeks(compareDate, rule.condition.offsetWeeks);
    }

    if (compareDates(taskADate, compareDate, rule.condition.operator)) {
      risks.push({
        id: rule.id,
        name: rule.name,
        description: rule.description,
        severity: rule.severity,
        phase: rule.phase,
        taskAName: taskA.name,
        taskADate: taskA.end,
        nodeBName: compareNodeName || undefined,
        nodeBDate: compareDate ? compareDate.toISOString().split('T')[0] : undefined,
      });
    }
  }

  return risks;
}

export function getRequiredFunctionalTasks(): FunctionalTask[] {
  const taskSet = new Set<FunctionalTask>();
  riskRuleDefinitions.forEach(rule => {
    taskSet.add(rule.condition.taskA);
    if (rule.condition.taskB) {
      taskSet.add(rule.condition.taskB);
    }
  });
  return Array.from(taskSet);
}

export function getRequiredFunctionalNodes(): FunctionalNodeType[] {
  const nodeSet = new Set<FunctionalNodeType>();
  riskRuleDefinitions.forEach(rule => {
    if (rule.condition.nodeB) {
      nodeSet.add(rule.condition.nodeB);
    }
  });
  return Array.from(nodeSet);
}
